import React, { useState, useEffect, useRef } from "react";
import type { KanbanCardData } from "./types";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Avatar from "../ui/Avatar";
import { LoadingIndicator } from "../chat";
import WorkerAvatar from "../worker/WorkerAvatar";
import Drawer from "../ui/drawer";
import { hexToRgba } from "@/utils/colorUtils";
import { sanitizeRichHtml } from "@/utils/sanitizeHtml";
import { Badge } from "../ui/badge/Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment } from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { podCurrentStatusToDisplayString } from "@/utils/podStatus";

interface KanbanCardProps {
  card: KanbanCardData;
  columnId: string;
  isOverlay?: boolean;
  columnColor?: string;
  variant?: "default" | "compact";
}

// const PULSE_SPEED_PX_PER_SEC = 60; // Removed
const TRACK_ANIMATION_DURATION_MS = 200;

export const KanbanCard: React.FC<KanbanCardProps> = ({
  card,
  columnId,
  isOverlay = false,
  columnColor,
  variant = "default",
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform: sortableTransform,
    transition: sortableTransition,
    isDragging,
  } = useSortable({
    id: card.id,
    data: { type: "card", card: card, columnId: columnId },
  });

  const [showPulseConcept, setShowPulseConcept] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const [isTrackAnimationComplete, setIsTrackAnimationComplete] =
    useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Effect 1: Determine if the pulse concept should be shown (after 1s delay)
  useEffect(() => {
    let timerId: NodeJS.Timeout | undefined = undefined;
    const shouldPulseConceptBeActive =
      columnId === "inprogress" && !isDragging && !isOverlay;

    if (shouldPulseConceptBeActive) {
      timerId = setTimeout(() => {
        setShowPulseConcept(true);
      }, 1000);
    } else {
      setShowPulseConcept(false);
      setIsTrackAnimationComplete(false); // Also reset track animation state if pulse concept is off
    }

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [columnId, isDragging, isOverlay, card.id]);

  // Effect 3: Control the track's slide-in animation and then signal completion
  useEffect(() => {
    let trackTimerId: NodeJS.Timeout | undefined = undefined;

    if (showPulseConcept) {
      trackTimerId = setTimeout(() => {
        setIsTrackAnimationComplete(true);
      }, TRACK_ANIMATION_DURATION_MS);
    } else {
      setIsTrackAnimationComplete(false);
    }

    return () => {
      if (trackTimerId) clearTimeout(trackTimerId);
    };
  }, [showPulseConcept]);

  // Handle drawer state
  const handleOpenDrawer = () => {
    // Do not open drawer if a drag is in progress
    if (isDragging) return;
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  // Determine if the track container itself should be rendered/visible
  const shouldShowTrack = showPulseConcept;

  const hoverBgColor = columnColor
    ? hexToRgba(columnColor, 0.1)
    : "rgba(100, 116, 139, 0.2)"; // fallback to slate-500 @ 20%

  const statusBadgeLabel = podCurrentStatusToDisplayString(
    card.status?.current_status
  );
  const stageTrackLabel = podCurrentStatusToDisplayString(
    card.status?.current_stage
  );
  const drawerStateLabel = podCurrentStatusToDisplayString(card.current_state);

  let style: React.CSSProperties = {
    "--hover-bg-color": hoverBgColor,
  } as React.CSSProperties;

  if (isOverlay) {
    style = {
      ...style,
      opacity: 1,
      cursor: "grabbing",
      zIndex: 100,
      transform: "scale(1)",
      boxShadow:
        "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
    };
  } else {
    style = {
      ...style,
      transform: CSS.Transform.toString(sortableTransform),
      transition:
        sortableTransition || "transform 200ms ease, box-shadow 200ms ease",
      opacity: isDragging ? 0.5 : 1,
      cursor: isDragging ? "grabbing" : "grab",
      ...(isDragging && {
        transform: `${CSS.Transform.toString(sortableTransform) || ""} `,
      }),
    };
  }

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
        onClick={card.onClick || handleOpenDrawer}
        className={`relative bg-white p-4 ${
          variant === "compact" ? "min-h-[100px]" : "min-h-[130px]"
        } rounded-lg break-words transition-shadow duration-200 select-none ${
          isOverlay
            ? "dark:bg-slate-800/90"
            : " dark:bg-slate-800/40 hover:[background-color:var(--hover-bg-color)]"
        } ${isDragging && !isOverlay ? " scale-[0.97] backface-hidden" : ""}`}
      >
        <div
          ref={trackRef}
          className={`absolute top-3 left-3 bottom-3 w-1 bg-slate-100 dark:bg-slate-600 rounded-full overflow-hidden transition-all duration-${TRACK_ANIMATION_DURATION_MS} ease-in-out ${
            shouldShowTrack ? "opacity-100" : "opacity-0"
          }`}
        >
          {shouldShowTrack && isTrackAnimationComplete && (
            <div className="absolute top-0 w-full h-8 rounded-full overflow-hidden animate-pulse-custom" />
          )}
        </div>

        <div
          className={`flex flex-col gap-3 transition-all duration-${TRACK_ANIMATION_DURATION_MS} ease-in-out ${
            shouldShowTrack ? "pl-4" : "pl-0"
          }`}
        >
          {/* Header with title and badge */}
          <div className="flex items-start justify-between gap-2">
            <h4
              className={`font-medium text-gray-800 dark:text-slate-100 flex-1`}
            >
              {card.title}
            </h4>
          </div>
          {/* Status Badge - Top Right */}
          {statusBadgeLabel && (
            <div className="flex items-center gap-2">
              <Badge color={card.status.badgeColor || "zinc"}>
                {statusBadgeLabel}
              </Badge>
            </div>
          )}

          {/* Content with speech bubble */}
          {card.content && (
            <div className="flex gap-2 items-start">
              <p className="text-sm text-gray-500 dark:text-slate-300 whitespace-pre-wrap">
                {card.content}
              </p>
            </div>
          )}
          {stageTrackLabel &&
            card.columnId === "inprogress" &&
            shouldShowTrack && (
              <div
                className={`bg-slate-100 dark:bg-slate-600 rounded p-2 leading-none px-2 mt-2 font-mono text-xs text-slate-500 dark:text-slate-300 flex items-center space-x-2 transition-opacity duration-${TRACK_ANIMATION_DURATION_MS} ease-in-out ${
                  shouldShowTrack ? "opacity-100" : "opacity-0"
                }`}
              >
                <div className="flex-grow">{stageTrackLabel}</div>
                <LoadingIndicator dotSize={3} dotSpacing={2} />
              </div>
            )}
          {(card.assigneeName || card.assigneeAvatarUrl) && (
            <div className="flex pt-2 mb-1 items-center space-x-2 border-slate-200 dark:border-slate-600 leading-none">
              <WorkerAvatar seed={card.assigneeName || "default"} size={20} />
              {card.assigneeName && (
                <div className="text-sm text-gray-500 dark:text-slate-400">
                  {card.assigneeName}
                </div>
              )}
            </div>
          )}

          {/* Instance Name Display */}
          {card.instanceName && (
            <div className=" italic ">
              <p className="text-sm  text-slate-400" title={card.instanceName}>
                {card.instanceName}
              </p>
            </div>
          )}
        </div>
      </div>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        title={card.title}
      >
        <div className="p-6 pt-0 space-y-4 divide-y divide-slate-200 dark:divide-slate-700">
          <div>
            {drawerStateLabel && (
              <div>
                <p className="mt-1  text-white whitespace-pre-wrap">
                  {drawerStateLabel}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 mt-12  pb-6 border-slate-200 dark:border-slate-700 ">
              {/* Assignee */}
              {(card.assigneeName || card.assigneeAvatarUrl) && (
                <div>
                  <h3 className="text-xs mb-2 font-mono text-gray-500 dark:text-slate-400 uppercase tracking-widest">
                    Assigned to
                  </h3>
                  <div className="mt-1 flex items-center space-x-2">
                    <WorkerAvatar
                      seed={card.assigneeName || "default"}
                      size={20}
                    />
                    <span className="text-gray-900 dark:text-slate-100">
                      {card.assigneeName || "Unassigned"}
                    </span>
                  </div>
                </div>
              )}
              {/* Column */}
              <div>
                <h3 className="text-xs mb-2 font-mono text-gray-500 dark:text-slate-400 uppercase tracking-widest">
                  Status
                </h3>
                <p className="mt-1 text-gray-900 dark:text-slate-100 capitalize">
                  {columnId}
                </p>
              </div>
            </div>
          </div>

          {card.notes && (
            <div>
              <h3 className="text-xs font-mono text-gray-500 dark:text-slate-400 uppercase mb-2 tracking-widest">
                Notes
              </h3>
              <p className="mt-1 text-gray-700 dark:text-slate-300 whitespace-pre-wrap">
                <div dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(card.notes) }}></div>
              </p>
            </div>
          )}

          {/* Instance Info */}
          {(card.instanceId || card.instanceName) && (
            <div>
              <h3 className="text-xs font-mono text-gray-500 dark:text-slate-400 uppercase tracking-widest">
                Instance
              </h3>
              <div className="mt-1 space-y-1">
                {card.instanceName && (
                  <p className="text-gray-900 dark:text-slate-100">
                    {card.instanceName}
                  </p>
                )}
                {card.instanceId && (
                  <p className="text-sm text-gray-600 dark:text-slate-400 font-mono">
                    ID: {card.instanceId}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Card ID */}
        </div>
      </Drawer>
    </>
  );
};
